import loadable from '@loadable/component'

import { cookieAllowlist } from './config/cookie-allowlist'
import { broadbandCrumbs, tvCrumbs, providersCrumbs, tvProvidersCrumbs, localCrumbs } from './config/crumbs'
import filterConfig from './config/filter-config'
import { mobileBroadbandTypesFormat } from './config/formatting'
import { orderings, phoneOrderings } from './config/orderings'
import { UP_TO } from './config/speeds'
import localRoutes from './local-routes'
import providerRoutes from './provider-routes'
import routesList from './routes-list'
import { isProd } from './server/helpers/env'

const Packages = loadable(() => import('./components/pages/packages'))
const Hull = loadable(() => import('./components/pages/hull'))
const HullResults = loadable(() => import('./components/pages/hull/results'))
const Homepage = loadable(() => import('./components/pages/homepage'))
const Journey = loadable(() => import('./components/pages/journey'))
const PostcodeCheckerLanding = loadable(() => import('./components/pages/postcode-checker-landing'))
const Providers = loadable(() => import('./components/pages/providers'))
const Products = loadable(() => import('./components/pages/products'))
const LocalLanding = loadable(() => import('./components/local/landing'))

const Routes = () => [
  {
    path: routesList.journey,
    component: Journey,
    questionLedJourney: true,
    crumbs: broadbandCrumbs('Journey'),
    scope: 'broadband',
    pageId: 'journey',
    cookieAllowlist,
    funnelHeader: true,
    noFooter: true,
    helmet: {
      metaContent: {
        'og:url': '//www.uswitch.com/broadband/journey',
        'og:title': 'Compare broadband deals - Exclusive broadband and TV packages from leading providers',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: routesList.broadband_homepage,
    component: Homepage,
    questionLedJourney: true,
    scope: 'broadband',
    slug: 'broadband_homepage',
    pageId: 'broadband',
    newsSlug: 'recent_broadband',
    cookieAllowlist,
    listnames: () => ({ table: 'Broadband - Our top 10 broadband packages' }),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/'
    },
    filterConfig: filterConfig({ specialOffers: false }),
    embeddedEeveeEntityIds: {
      heroContent: '1fJvXjMT64fd5fOiPdP98S',
      modularPage: '1joM5CRjF2YGkqLwru39XQ',
      author: '1iQj37f6RmSVdm0rCFpRCT',
      reviews: '3fi1wINvWdK25zAcshlwKl'
    },
    returnUrl: '/broadband/results/',
    showDealsTable: true,
    orderings
  },
  {
    path: routesList.tv_homepage,
    component: Homepage,
    scope: 'tv',
    newsSlug: 'recent_tv',
    pageId: 'tv',
    cookieAllowlist,
    listnames: () => ({ table: 'TV - Homepage Promos' }),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/tv/'
    },
    embeddedEeveeEntityIds: {
      heroContent: '1ZSclBQug0JEoTcyMEEwc5',
      modularPage: '7BEgktEwWfRxw5KTG5jx69'
    },
    returnUrl: '/tv/packages/'
  },
  {
    path: routesList.providers,
    component: Providers,
    exact: true,
    scope: 'broadband',
    crumbs: providersCrumbs(),
    newsSlug: 'recent_broadband',
    pageId: 'broadband-providers',
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/providers/'
    }
  },
  {
    path: routesList.tv_providers,
    component: Providers,
    exact: true,
    scope: 'tv',
    crumbs: tvProvidersCrumbs(),
    newsSlug: 'recent_tv',
    pageId: 'tv-providers',
    helmet: {
      canonical: 'https://www.uswitch.com/tv/providers/'
    }
  },
  {
    path: routesList.local_hull,
    component: isProd ? null : Hull,
    hideTabs: false,
    questionLedJourney: true,
    slug: 'hull',
    pageId: 'local-hull',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    crumbs: localCrumbs(),
    exact: true,
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      table: 'Broadband - Our top 10 broadband packages'
    }),
    orderings,
    filterConfig: filterConfig(),
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/local/hull'
    },
    returnUrl: '/broadband/local/hull'
  },
  {
    path: routesList.hull_results,
    component: isProd ? null : HullResults,
    hideTabs: false,
    questionLedJourney: true,
    slug: 'hull',
    pageId: 'local-hull',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    crumbs: localCrumbs(),
    exact: true,
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      table: 'Broadband - Our top 10 broadband packages'
    }),
    orderings,
    filterConfig: filterConfig(),
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/local/hull/results'
    },
    returnUrl: '/broadband/local/hull'
  },
  {
    path: routesList.broadband_results,
    component: Packages,
    hideTabs: false,
    questionLedJourney: true,
    slug: 'packages',
    pageId: 'broadband-deals',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 broadband packages'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Your broadband deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/results/',
      metaContent: {
        robots: 'noindex,nofollow'
      }
    },
    returnUrl: '/broadband/results/',
    embeddedEeveeEntityIds: {
      heroContent: '4syoyac8hs1C5pTEUqJlVI',
      modularPage: '3SwBxoGHkz25t4jinKx8hi'
    },
    hideP0Image: false
  },
  {
    path: routesList.fibre_optic,
    component: Packages,
    questionLedJourney: true,
    slug: 'fibre_optic',
    pageId: 'broadband-compare-fibre-optic',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 fibre optic broadband deals'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Top 10 fibre optic broadband deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/compare/fibre_optic/'
    },
    returnUrl: '/broadband/compare/fibre_optic/',
    embeddedEeveeEntityIds: {
      heroContent: '58FNV5eyxdB9DJ83FEyGzG',
      modularPage: '18QLYQJiIfZZXzcby3AWRf'
    }
  },
  {
    path: routesList.student_broadband,
    component: Packages,
    slug: 'student_broadband',
    pageId: 'student-broadband',
    newsSlug: 'recent_broadband',
    nonServiceabilityPage: true,
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 student TV and broadband deals'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Our top 10 student TV and broadband deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/student-broadband/'
    }
  },
  {
    path: routesList.broadband_and_free_line_rental,
    component: Packages,
    questionLedJourney: true,
    slug: 'broadband_and_free_line_rental',
    pageId: 'broadband-and-free-line-rental',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 broadband deals with free line rental'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Our top 10 broadband deals + free line rental'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/broadband-and-free-line-rental/'
    },
    returnUrl: '/broadband/broadband-and-free-line-rental/'
  },
  {
    path: routesList.business_broadband,
    component: Packages,
    nonServiceabilityPage: true,
    slug: 'business_broadband',
    pageId: 'business-broadband',
    newsSlug: 'recent_broadband',
    speeds: UP_TO,
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 business broadband deals'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Our top 10 business broadband deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/business-broadband/'
    },
    connectionTypes: ['BUSINESS'],
    embeddedEeveeEntityIds: {
      heroContent: '3kqOxYh1vW4VEocaRx4k0v',
      modularPage: '1XeiqVqBs5HAHaB6bqgngM',
      author: '1iQj37f6RmSVdm0rCFpRCT',
      reviews: '3fi1wINvWdK25zAcshlwKl'
    },
    hideP0Image: false
  },
  {
    path: routesList.mobile_broadband,
    component: Packages,
    slug: 'mobile_broadband',
    pageId: 'mobile-broadband',
    newsSlug: 'recent_broadband',
    nonServiceabilityPage: true,
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 mobile broadband packages'
    }),
    orderings,
    filterConfig: filterConfig({ broadbandTypesFormat: mobileBroadbandTypesFormat }),
    crumbs: broadbandCrumbs('Our top 10 mobile broadband deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/mobile-broadband/'
    }
  },
  {
    path: routesList.home_phone_and_landline,
    component: Packages,
    nonServiceabilityPage: true,
    slug: 'home_phone_and_landline',
    pageId: 'home-phone-and-landline',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Home phone & landline deals'
    }),
    orderings: phoneOrderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Home phone & landline deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/compare/home_phone_and_landline/'
    }
  },
  {
    path: routesList.broadband_and_digital_tv,
    component: Packages,
    questionLedJourney: true,
    slug: 'broadband_and_digital_tv',
    pageId: 'tv-broadband-deals',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'tv_purchases') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - TV & broadband deals'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('TV & broadband deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/compare/broadband_and_digital_tv/'
    },
    returnUrl: '/broadband/compare/broadband_and_digital_tv/',
    embeddedEeveeEntityIds: {
      heroContent: '62rIfCMvrVcFbFsvNzRHNC',
      modularPage: '2xak91Qa2lnIGjp5UtNxbB'
    }
  },
  {
    path: routesList.broadband_and_home_phone,
    component: Packages,
    questionLedJourney: true,
    slug: 'broadband_and_home_phone',
    pageId: 'broadband-home-phone-deals',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Broadband & home phone deals'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Broadband & home phone deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/broadband-and-home-phone/'
    },
    returnUrl: '/broadband/broadband-and-home-phone/',
    embeddedEeveeEntityIds: {
      heroContent: '3DmfZHozbQ2rHQOYz1Fu4f',
      modularPage: '1eh2SrZL39LSXVKGwtWKSR',
      author: '1iQj37f6RmSVdm0rCFpRCT'
    }
  },
  {
    path: routesList.cheap_broadband,
    component: Packages,
    questionLedJourney: true,
    slug: 'cheap_broadband',
    pageId: 'cheap-broadband',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 cheap broadband deals'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Our top 10 cheap broadband deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/cheap-broadband/'
    },
    returnUrl: '/broadband/cheap-broadband/',
    embeddedEeveeEntityIds: {
      heroContent: '4RmbQzB9ROuhPce77BLn8O',
      modularPage: '2ud3YpAHCU5MgrPgLTOl3w'
    }
  },
  {
    path: routesList.broadband_only,
    component: Packages,
    questionLedJourney: true,
    slug: 'broadband_only',
    pageId: 'broadband-only',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 broadband only deals'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Our top 10 broadband only deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/broadband-only/'
    },
    returnUrl: '/broadband/broadband-only/'
  },
  {
    path: routesList.wireless_broadband,
    component: Packages,
    questionLedJourney: true,
    slug: 'wireless_broadband',
    pageId: 'wireless-broadband',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 wireless broadband deals'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Our top 10 wireless broadband deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/wireless-broadband/'
    },
    returnUrl: '/broadband/wireless-broadband/'
  },
  {
    path: routesList.free_broadband,
    component: Packages,
    slug: 'free_broadband',
    nonServiceabilityPage: true,
    pageId: 'no-upfront-cost-broadband',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 broadband deals with no setup cost'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Our top 10 broadband deals with no setup cost'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/no-upfront-cost-broadband/'
    }
  },
  {
    path: routesList.unlimited_broadband,
    component: Packages,
    slug: 'unlimited_broadband',
    pageId: 'unlimited-broadband',
    newsSlug: 'recent_broadband',
    nonServiceabilityPage: true,
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 unlimited broadband deals'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Our top 10 unlimited broadband deals'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/unlimited-broadband/'
    }
  },
  {
    path: routesList.home_phone_and_international_calls,
    component: Packages,
    nonServiceabilityPage: true,
    slug: 'home_phone_and_international_calls',
    pageId: 'broadband-compare-home-phone-and-international-calls',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top home landline and international calls broadband deals'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Cheap international calls'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/compare/home_phone_and_international_calls/'
    }
  },
  {
    path: routesList.free_router,
    component: Packages,
    questionLedJourney: true,
    slug: 'free_router',
    pageId: 'broadband-compare-free-router',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our top 10 broadband deals + free wireless router'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Our top 10 broadband deals + free wireless router'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/compare/free_router/'
    },
    returnUrl: '/broadband/compare/free_router/'
  },
  {
    path: routesList.broadband_with_free_gifts,
    component: Packages,
    questionLedJourney: true,
    slug: 'broadband_with_free_gifts',
    pageId: 'broadband-compare-broadband-with-free-gifts',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Our Top 10 Broadband Deals + Free Gifts'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Our Top 10 Broadband Deals + Free Gifts'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/compare/broadband_with_free_gifts/'
    },
    returnUrl: '/broadband/compare/broadband_with_free_gifts/'
  },
  {
    path: routesList.black_friday,
    component: Packages,
    questionLedJourney: true,
    slug: 'black_friday',
    pageId: 'broadband-compare-black-friday',
    newsSlug: 'recent_broadband',
    params: { specialOffers: 'BLACK_FRIDAY' },
    scope: 'broadband',
    cookieAllowlist,
    listnames: (intent = 'provider_of_the_week') => ({
      p0: 'Broadband - Header Advert',
      p1: `Broadband - Ad - ${intent.toLowerCase()}`,
      p4: `Broadband - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'Broadband - Black Friday broadband deals'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: broadbandCrumbs('Black Friday broadband deals 2022'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/compare/black_friday/'
    },
    returnUrl: '/broadband/compare/black_friday/'
  },
  {
    path: routesList.tv_packages,
    component: Packages,
    hideTabs: false,
    slug: 'tv/packages',
    pageId: 'tv-packages',
    nonServiceabilityPage: true,
    newsSlug: 'recent_tv',
    scope: 'tv',
    cookieAllowlist,
    listnames: (intent = 'tv_purchases') => ({
      p0: 'TV - Header Advert',
      p1: `TV - Ad - ${intent.toLowerCase()}`,
      p4: `TV - Ad - ${intent.toLowerCase()} - enhanced`,
      table: 'TV - TV Packages'
    }),
    orderings,
    filterConfig: filterConfig(),
    crumbs: tvCrumbs,
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/tv/packages/',
      metaContent: {
        'og:url': '//www.uswitch.com/tv/packages/',
        'og:title': 'Compare TV Packages - Find Cheap TV Deals & Bundles'
      }
    }
  },
  {
    path: routesList.broadband_products,
    component: Products,
    slug: 'broadband/products',
    pageId: 'broadband-products',
    newsSlug: 'recent_broadband',
    scope: 'broadband',
    cookieAllowlist,
    orderings,
    crumbs: broadbandCrumbs('Broadband products'),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/products',
      metaContent: {
        'og:url': '//www.uswitch.com/broadband/products',
        'og:title': 'Compare broadband deals - Exclusive broadband and TV packages from leading providers'
      }
    }
  },
  {
    path: routesList.tv_postcode_checker_landing,
    component: PostcodeCheckerLanding,
    pageId: 'tv-postcode-checker-landing',
    scope: 'tv',
    cookieAllowlist,
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/tv/postcode_checker/'
    },
    embeddedEeveeEntityIds: {
      heroContent: '291imKac8Avj9fvXh3qnwv',
      modularPage: 'tpg3oAUpvCLs7KkRZJwCi'
    }
  },
  {
    path: routesList.postcode_checker_landing,
    component: PostcodeCheckerLanding,
    pageId: 'postcode-checker-landing',
    scope: 'broadband',
    cookieAllowlist,
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/postcode_checker/'
    },
    embeddedEeveeEntityIds: {
      heroContent: 'HVy2oDAqFGxI2aNtDn4nR',
      modularPage: '2tIzGrH4YFUJKXqZbM1ahj'
    }
  },
  {
    path: routesList.local_landing,
    component: LocalLanding,
    pageId: 'local-landing',
    nonServiceabilityPage: true,
    scope: 'broadband',
    cookieAllowlist,
    crumbs: localCrumbs(),
    exact: true,
    helmet: {
      canonical: 'https://www.uswitch.com/broadband/local/'
    }
  },
  ...providerRoutes,
  ...localRoutes
]

export default Routes
